import { createElement } from 'react';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';

export interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: {
    name: string;
    url?: string;
  };
  tags?: string[];
  canonicalUrl?: string;
}

export function SEOHead(props: SEOProps) {
  const siteName = 'AISEOBlogGenerator';
  const twitterHandle = '@makershelpdesk';
  const defaultImage = 'https://aiseobloggenerator.com/og-image.jpg';
  
  // Sanitize all inputs for security
  const safeTitle = DOMPurify.sanitize(props.title);
  const safeDescription = DOMPurify.sanitize(props.description);
  const safeImage = props.image ? DOMPurify.sanitize(props.image) : defaultImage;
  
  // Validate URL for security
  let safeUrl = '';
  try {
    const urlToUse = props.url || (typeof window !== 'undefined' ? window.location.href : '');
    const parsedUrl = new URL(urlToUse);
    if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
      safeUrl = urlToUse;
    }
  } catch (e) {
    safeUrl = typeof window !== 'undefined' ? window.location.href : '';
  }
  
  // Sanitize author information
  const safeAuthor = props.author ? {
    name: DOMPurify.sanitize(props.author.name),
    url: props.author.url ? DOMPurify.sanitize(props.author.url) : undefined
  } : undefined;
  
  // Sanitize tags
  const safeTags = props.tags?.map(tag => DOMPurify.sanitize(tag));
  
  // Safe canonical URL
  const safeCanonicalUrl = props.canonicalUrl ? DOMPurify.sanitize(props.canonicalUrl) : safeUrl;

  // Generate JSON-LD schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': props.type === 'article' ? 'Article' : 'WebPage',
    headline: safeTitle,
    description: safeDescription,
    image: safeImage,
    url: safeUrl,
    datePublished: props.publishedTime,
    dateModified: props.modifiedTime || props.publishedTime,
    author: safeAuthor ? {
      '@type': 'Person',
      name: safeAuthor.name,
      url: safeAuthor.url
    } : {
      '@type': 'Person',
      name: 'Gabe Fletcher',
      url: 'https://bolters.io'
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      logo: {
        '@type': 'ImageObject',
        url: 'https://aiseobloggenerator.com/logo.png'
      }
    },
    keywords: safeTags?.join(', '),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': safeUrl
    }
  };

  // Construct the full title string
  const fullTitle = `${safeTitle} | ${siteName}`;

  // Build children array for Helmet
  const helmetChildren = [
    createElement('title', null, fullTitle),
    createElement('meta', { name: 'description', content: safeDescription }),
    createElement('link', { rel: 'canonical', href: safeCanonicalUrl }),
    
    // Open Graph
    createElement('meta', { property: 'og:title', content: safeTitle }),
    createElement('meta', { property: 'og:description', content: safeDescription }),
    createElement('meta', { property: 'og:type', content: props.type || 'website' }),
    createElement('meta', { property: 'og:url', content: safeUrl }),
    createElement('meta', { property: 'og:site_name', content: siteName }),
    createElement('meta', { property: 'og:image', content: safeImage }),
    createElement('meta', { property: 'og:image:width', content: '1200' }),
    createElement('meta', { property: 'og:image:height', content: '630' }),
    createElement('meta', { property: 'og:image:alt', content: safeTitle }),
    
    // Twitter Card
    createElement('meta', { name: 'twitter:card', content: 'summary_large_image' }),
    createElement('meta', { name: 'twitter:site', content: twitterHandle }),
    createElement('meta', { name: 'twitter:creator', content: twitterHandle }),
    createElement('meta', { name: 'twitter:title', content: safeTitle }),
    createElement('meta', { name: 'twitter:description', content: safeDescription }),
    createElement('meta', { name: 'twitter:image', content: safeImage }),
    createElement('meta', { name: 'twitter:image:alt', content: safeTitle }),
    
    // Additional SEO tags
    createElement('meta', { 
      name: 'robots', 
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' 
    }),
    createElement('meta', { name: 'googlebot', content: 'index, follow' }),
    
    // Structured Data
    createElement('script', { 
      type: 'application/ld+json',
      dangerouslySetInnerHTML: { __html: JSON.stringify(schema) }
    })
  ];

  // Add conditional elements
  if (props.publishedTime) {
    helmetChildren.push(
      createElement('meta', { property: 'article:published_time', content: props.publishedTime })
    );
  }
  
  if (props.modifiedTime) {
    helmetChildren.push(
      createElement('meta', { property: 'article:modified_time', content: props.modifiedTime })
    );
  }
  
  if (safeAuthor?.name) {
    helmetChildren.push(
      createElement('meta', { property: 'article:author', content: safeAuthor.name }),
      createElement('meta', { name: 'author', content: safeAuthor.name })
    );
  }
  
  if (safeTags) {
    safeTags.forEach((tag, index) => {
      helmetChildren.push(
        createElement('meta', { key: index, property: 'article:tag', content: tag })
      );
    });
  }

  return createElement(Helmet, null, ...helmetChildren);
}

// Hook for easier usage
export function useSEO(props: SEOProps) {
  return SEOHead(props);
}